import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { FieldArray, Formik, FormikHelpers } from 'formik';
import { array, object, string } from 'yup';
import {
  createCustomField,
  deleteCustomField,
  updateCustomField,
  useActor,
} from '../../lib/audited-writes';
import { useFirebaseContext } from '../../lib/firebase-context';
import { useToast } from '../../lib/toast-context';
import type { Attendee, CustomField } from '../../lib/types';

const FIELD_NAME_MAX_LENGTH = 50;
const OPTION_LABEL_MAX_LENGTH = 30;

type CustomFieldFormValues = {
  name: string;
  options: string[];
};

const blankValues: CustomFieldFormValues = { name: '', options: [''] };

// Counts attendees with a specific option assigned for a given field.
const countAttendeesWithOption = (
  attendees: Attendee[],
  fieldRef: string,
  option: string,
): number =>
  attendees.filter((a) => a.customFields?.[fieldRef] === option).length;

// Counts attendees with any value for a given field.
const countAttendeesWithField = (
  attendees: Attendee[],
  fieldRef: string,
): number =>
  attendees.filter(
    (a) => a.customFields && fieldRef in a.customFields,
  ).length;

const buildSchema = (
  existingFields: CustomField[],
  excludeRef: string | undefined,
  attendees: Attendee[],
) =>
  object().shape({
    name: string()
      .trim()
      .required('Name is required')
      .max(
        FIELD_NAME_MAX_LENGTH,
        `Must be ${FIELD_NAME_MAX_LENGTH} characters or less`,
      )
      .test('unique', 'A field with this name already exists', (v) => {
        if (!v) return true;
        return !existingFields.some(
          (f) =>
            f.ref !== excludeRef &&
            f.name.toLowerCase() === v.trim().toLowerCase(),
        );
      }),
    options: array()
      .of(
        string()
          .trim()
          .required('Option label is required')
          .max(
            OPTION_LABEL_MAX_LENGTH,
            `Must be ${OPTION_LABEL_MAX_LENGTH} characters or less`,
          ),
      )
      .min(1, 'At least one option is required')
      .test('unique-options', 'Option labels must be unique', (opts) => {
        if (!opts) return true;
        const trimmed = opts
          .filter((o): o is string => typeof o === 'string')
          .map((o) => o.trim().toLowerCase());
        return trimmed.length === new Set(trimmed).size;
      })
      .test(
        'no-removal-of-assigned-options',
        // Message is computed dynamically inside the test; this is a fallback.
        'An option in use was removed',
        function (newOptions) {
          if (!excludeRef) return true; // create flow, nothing to compare against
          const before = existingFields.find((f) => f.ref === excludeRef);
          if (!before) return true;
          // Compare case-insensitively to match `unique-options` so a pure
          // casing change ("Red" -> "red") reads as a rename rather than
          // a removal. Any orphaned attendee values (storage is verbatim;
          // we don't rename stored values) surface in the attendee form
          // as a disabled "no longer an option" MenuItem.
          const newSetLower = new Set(
            (newOptions ?? [])
              .filter((o): o is string => typeof o === 'string')
              .map((o) => o.trim().toLowerCase()),
          );
          for (const old of before.options) {
            if (!newSetLower.has(old.trim().toLowerCase())) {
              const inUse = countAttendeesWithOption(
                attendees,
                excludeRef,
                old,
              );
              if (inUse > 0) {
                return this.createError({
                  message: `Option "${old}" is assigned to ${inUse} attendee${inUse === 1 ? '' : 's'}. Clear it from those attendees before removing the option.`,
                });
              }
            }
          }
          return true;
        },
      ),
  });

const CustomFieldFormDialog = ({
  open,
  field,
  fields,
  attendees,
  onClose,
  onSaved,
  onError,
}: {
  open: boolean;
  field: CustomField | null;
  fields: CustomField[];
  attendees: Attendee[];
  onClose: () => void;
  onSaved: (mode: 'add' | 'edit') => void;
  onError: (msg: string) => void;
}) => {
  const actor = useActor();
  const isEdit = field !== null;
  const [isWorking, setIsWorking] = useState(false);

  const handleClose = () => {
    if (isWorking) return;
    onClose();
  };

  const initialValues = useMemo<CustomFieldFormValues>(
    () =>
      field
        ? { name: field.name, options: [...field.options] }
        : blankValues,
    [field],
  );

  const validationSchema = useMemo(
    () => buildSchema(fields, field?.ref, attendees),
    [fields, field?.ref, attendees],
  );

  const handleSubmit = async (
    values: CustomFieldFormValues,
    { setSubmitting }: FormikHelpers<CustomFieldFormValues>,
  ) => {
    setIsWorking(true);
    let succeeded = false;
    try {
      const payload = {
        name: values.name.trim(),
        options: values.options.map((o) => o.trim()),
      };
      if (field) {
        await updateCustomField(actor, field, payload);
      } else {
        await createCustomField(actor, payload);
      }
      succeeded = true;
    } catch (error) {
      console.error('Error saving custom field', error);
      onError(
        isEdit ? 'Failed to update custom field' : 'Failed to add custom field',
      );
    } finally {
      setSubmitting(false);
      setIsWorking(false);
    }
    if (succeeded) {
      onSaved(isEdit ? 'edit' : 'add');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>{isEdit ? 'Edit custom field' : 'Add custom field'}</DialogTitle>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Stack spacing={2}>
                <TextField
                  name='name'
                  label='Field name'
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && Boolean(errors.name)}
                  helperText={
                    (touched.name && errors.name) ||
                    `${values.name.length}/${FIELD_NAME_MAX_LENGTH}`
                  }
                  inputProps={{ maxLength: FIELD_NAME_MAX_LENGTH }}
                  disabled={isSubmitting}
                  fullWidth
                  autoFocus
                />
                <Box>
                  <Typography variant='subtitle2' sx={{ mb: 1 }}>
                    Options
                  </Typography>
                  <FieldArray name='options'>
                    {(helpers) => (
                      <Stack spacing={1}>
                        {values.options.map((option, idx) => {
                          // errors.options is either an array of per-row errors or a single
                          // array-level string (e.g. unique-options or removal-blocked). When
                          // it's a string, JS indexing returns characters; guard with
                          // Array.isArray so per-row helperText only reflects real per-row errors.
                          const fieldErr = Array.isArray(errors.options)
                            ? (errors.options[idx] as string | undefined)
                            : undefined;
                          const fieldTouched = Array.isArray(touched.options)
                            ? touched.options[idx]
                            : false;
                          return (
                            <Box
                              key={idx}
                              sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}
                            >
                              <TextField
                                name={`options.${idx}`}
                                label={`Option ${idx + 1}`}
                                value={option}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={fieldTouched && Boolean(fieldErr)}
                                helperText={fieldTouched && fieldErr}
                                inputProps={{ maxLength: OPTION_LABEL_MAX_LENGTH }}
                                disabled={isSubmitting}
                                fullWidth
                                size='small'
                              />
                              <IconButton
                                size='small'
                                aria-label={`Remove option ${idx + 1}`}
                                onClick={() => helpers.remove(idx)}
                                disabled={isSubmitting || values.options.length <= 1}
                                sx={{ mt: 0.5 }}
                              >
                                <Delete fontSize='small' />
                              </IconButton>
                            </Box>
                          );
                        })}
                        {/*
                          The array-level error string (e.g. unique-options
                          conflict, or option-removal-blocked) is rendered
                          here so it isn't tied to a single row's helperText.
                        */}
                        {typeof errors.options === 'string' && (
                          <Typography variant='caption' color='error'>
                            {errors.options}
                          </Typography>
                        )}
                        <Button
                          type='button'
                          startIcon={<Add />}
                          onClick={() => helpers.push('')}
                          disabled={isSubmitting}
                          size='small'
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          Add option
                        </Button>
                      </Stack>
                    )}
                  </FieldArray>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type='submit' variant='contained' disabled={isSubmitting}>
                {isEdit ? 'Save' : 'Add custom field'}
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

const DeleteCustomFieldDialog = ({
  field,
  attendees,
  onClose,
  onDeleted,
  onError,
}: {
  field: CustomField | null;
  attendees: Attendee[];
  onClose: () => void;
  onDeleted: () => void;
  onError: (msg: string) => void;
}) => {
  const actor = useActor();
  const [isWorking, setIsWorking] = useState(false);

  // Active-event count only — context attendees are scoped to the active
  // event. With soft-delete this is informational, not destructive: stored
  // values on inactive-event attendees are also preserved untouched.
  const affectedCount = field
    ? countAttendeesWithField(attendees, field.ref)
    : 0;

  const handleClose = () => {
    if (isWorking) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!field) return;
    setIsWorking(true);
    try {
      await deleteCustomField(actor, field);
      setIsWorking(false);
      onDeleted();
      onClose();
    } catch (error) {
      console.error('Error deleting custom field', error);
      onError('Failed to delete custom field');
      setIsWorking(false);
    }
  };

  return (
    <Dialog open={Boolean(field)} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>Delete custom field</DialogTitle>
      <DialogContent>
        {field && (
          <DialogContentText>
            Delete <strong>{field.name}</strong>? It will be hidden from every
            form and list.
            {affectedCount > 0
              ? ` ${affectedCount} attendee${affectedCount === 1 ? ' has a value' : 's have values'} for it; those values are preserved on each attendee in case the field is restored later.`
              : ' No attendees in the active event have a value for it.'}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isWorking}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color='error'
          variant='contained'
          disabled={isWorking}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ManageCustomFields = () => {
  const { customFields, attendees } = useFirebaseContext();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<CustomField | null>(null);
  const [deleting, setDeleting] = useState<CustomField | null>(null);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant='contained'
          startIcon={<Add />}
          onClick={() => setAdding(true)}
        >
          Add custom field
        </Button>
      </Box>
      {customFields.length === 0 ? (
        <Typography color='text.secondary' textAlign='center'>
          No custom fields yet. Click <strong>Add custom field</strong> to add one.
        </Typography>
      ) : (
        <List dense disablePadding>
          {customFields.map((field) => {
            const assignedCount = countAttendeesWithField(attendees, field.ref);
            return (
              <ListItem
                key={field.ref}
                divider
                sx={{
                  py: 0.25,
                  px: 1,
                  gap: 1,
                  '&:last-child': { borderBottom: 0 },
                }}
                secondaryAction={
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      edge='end'
                      size='small'
                      aria-label={`Edit ${field.name}`}
                      onClick={() => setEditing(field)}
                    >
                      <Edit fontSize='small' />
                    </IconButton>
                    <IconButton
                      edge='end'
                      size='small'
                      aria-label={`Delete ${field.name}`}
                      onClick={() => setDeleting(field)}
                    >
                      <Delete fontSize='small' />
                    </IconButton>
                  </Box>
                }
              >
                <Typography
                  variant='body2'
                  sx={{ minWidth: 200, flexShrink: 0, fontWeight: 500 }}
                  noWrap
                >
                  {field.name}
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ flexGrow: 1 }}
                >
                  {field.options.length} option{field.options.length === 1 ? '' : 's'}
                  {' \u00b7 '}
                  {assignedCount} assigned
                </Typography>
              </ListItem>
            );
          })}
        </List>
      )}

      <CustomFieldFormDialog
        open={adding || editing !== null}
        field={editing}
        fields={customFields}
        attendees={attendees}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
        onSaved={(mode) =>
          toast.success(mode === 'add' ? 'Custom field added' : 'Custom field saved')
        }
        onError={(msg) => toast.error(msg)}
      />

      <DeleteCustomFieldDialog
        field={deleting}
        attendees={attendees}
        onClose={() => setDeleting(null)}
        onDeleted={() => toast.success('Custom field deleted')}
        onError={(msg) => toast.error(msg)}
      />
    </>
  );
};

export default ManageCustomFields;
